import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import {

    takeUntil, map, filter, tap, first, switchMap, shareReplay,
} from 'rxjs/operators';

import { AddressService } from '@api/address/address.service';
import { Address } from '@api/address/models/address.models';
import { ShippingService } from '@api/shipping/shipping.service';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { SetChangesActiveButton } from '@pages/my-account/sections/changes/store/order-change/order-change.actions';
import { SetSelectedAddress } from '@shared/address/store/address/address.actions';
import { getSelectedAddress } from '@shared/address/store/address/address.selectors';
import { EventData } from '@shared/analytics/models/general/general.models';
import { ImShippingService } from '@shared/analytics/services/shipping/im-shipping.service';
import { ShippingEvent } from '@shared/analytics/store/analytics.actions';
import { NavigationConstants } from '@shared/navigation/constants/navigation.constants';
import { GoToInternalPage } from '@shared/navigation/store/navigation.actions';
import { InitButton, SetActiveButton } from '@shared/navigation-button/store/navigation-button.actions';
import { isNavigationActive, isNavigationNextStep } from '@shared/navigation-button/store/navigation-button.selectors';
import { getSelectedShippingAddress } from '@shared/shipping/store/shipping.selectors';

type isNewAddress = boolean;

@Component({
    selector: 'checkout-shipping-delivery-address-list',
    templateUrl: './address-list.component.html',
    styleUrls: ['./address-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * Componente para mostrar direcciones para el delivery
 */
export class CheckoutShippingDeliveryAddressListComponent implements OnInit, OnDestroy {
    private readonly onDestroy$ = new Subject<void>();

    @Output() public readonly editAddressCommand = new EventEmitter<isNewAddress>();

    public addresses$: Observable<Address[]>;

    public addressesLength: number;

    private isNavigationActive: boolean = false;

    private selectedAddress: Address;

    private readonly selectedAddress$: Observable<Address> = this.store.select(getSelectedShippingAddress);

    constructor(
        private readonly addressService: AddressService,
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        // private readonly modalService: DialogService,
        public shippingService: ShippingService,
        @Optional() public imShippingService: ImShippingService,
    ) {}

    public ngOnInit(): void {
        this.buttonManagement();
        this.addresses$ = this.addressService.getAddressesDelivery().pipe(
            tap((addresses) => { this.addressesLength = addresses.length; }),
            switchMap((addresses) => {
                if (this.shippingService.isNewAddress) {
                    return of(addresses.slice().reverse());
                }
                return of(addresses);
            }),
            shareReplay(1)
        );
        // Solo utilizamos el store para consultar la dirección ya guardada de antemano
        // La dirección seleccionada no se guarda en la store hasta que se llega al último del flujo de delivery
        this.store
            .select(getSelectedAddress)
            .pipe(first())
            .subscribe((selectedAddress: Address) => {
                this.selectedAddress = selectedAddress;
                this.shippingService.setSelectedAddress(selectedAddress);
            });

        this.selectedAddress$.pipe(
            filter((selectedAddress) => !!selectedAddress && !this.isNavigationActive),
            tap((selectedAddress) => {
                if (!this.selectedAddress) {
                    this.selectedAddress = selectedAddress;
                }
            }),
            takeUntil(this.onDestroy$)
        )
            .subscribe(() => {
                this.store.dispatch(SetActiveButton());
            });

        this.addresses$
            .pipe(
                tap((addresses) => { this.addressesLength = addresses.length; }),
                filter((addresses) => Boolean(addresses?.[0])),
                map((addresses) => addresses[0]),
                takeUntil(this.onDestroy$)
            )
            .subscribe(() => {
                this.shippingService.isNewAddress = false;
                this.selectAddress(this.selectedAddress);
            });
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    private buttonManagement() {
        this.store.dispatch(InitButton());

        this.store
            .select(isNavigationActive)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((isActive: boolean) => { this.isNavigationActive = isActive; });

        this.store
            .select(isNavigationNextStep)
            .pipe(
                filter((isNextStep: boolean) => isNextStep),
                takeUntil(this.onDestroy$)
            )
            .subscribe(() => {
                this.shippingService.setSelectedAddress(this.selectedAddress);
                this.store.dispatch(GoToInternalPage({ url: NavigationConstants.deliverySummary }));
            });
    }

    public selectAddress(selectedAddress: Address) {
        this.selectedAddress = selectedAddress;
        this.shippingService.setSelectedAddress(selectedAddress);
        // Como el popup bloquea interacción, da igual que se habilite el botón antes
        this.store.select(routerPublicQueries.getUrlFromRouter).pipe(
            takeUntil(this.onDestroy$)
        ).subscribe((url)=> {
            if (!this.isNavigationActive && !url.includes('changes') && this.selectedAddress) {
                this.store.dispatch(SetActiveButton());
            } else if (!this.isNavigationActive && url.includes('changes') && this.selectedAddress) {
                this.store.dispatch(SetChangesActiveButton({ status: true }));
            }
        });
    }

    public editAddress(address: Address): void {
        const eventData: EventData = {
            cf: 'checkout',
            action: address ? 'modificar_datos_envio' : 'anadir_direccion',
        };

        // eslint-disable-next-line deprecation/deprecation
        this.storeAddressTypeInImService(address);

        this.store.dispatch(ShippingEvent({ eventData }));
        this.store.dispatch(SetSelectedAddress({ selectedAddress: address }));

        this.editAddressCommand.emit(!address);
    }

    /**
     * @deprecated - Se debe refactorizar para desacoplar el servicio de analytics
     */
    private storeAddressTypeInImService(address: Address) {
        if (!this.imShippingService) {
            return;
        }

        if (address) {
            this.imShippingService.setIsNewAddress(false);
        } else {
            this.imShippingService.setIsNewAddress(true);
        }
    }
}
